import axios from "axios";
import {
    DohopAirShoppingRequest,
    DohopAirShoppingResponse,
    DohopSearchPassenger,
    AirportOption,
} from "../types/dohop";

// Movmo API configuration - proxy to Dohop to avoid CORS issues
const MOVMO_API_URL =
    process.env.REACT_APP_MOVMO_API_URL || "https://e2e.api.movmo.io";

// Search for flights via Movmo proxy to Dohop AirShopping API
export const searchDohopFlights = async (
    request: DohopAirShoppingRequest
): Promise<DohopAirShoppingResponse> => {
    const response = await axios.post<DohopAirShoppingResponse>(
        `${MOVMO_API_URL}/v1/dohop/air-shopping`,
        request,
        {
            headers: {
                "Content-Type": "application/json",
                "X-Api-Key": process.env.REACT_APP_MOVMO_API_KEY || "",
            },
        }
    );
    return response.data;
};

// Build search request from form state
export const buildSearchRequest = (
    origin: string,
    destination: string,
    departureDate: string,
    adults: number,
    children: number = 0,
    infants: number = 0
): DohopAirShoppingRequest => {
    const passengers: DohopSearchPassenger[] = [];

    // Add adult passengers
    for (let i = 0; i < adults; i++) {
        passengers.push({
            pax_id: `ADT${i + 1}`,
            expected_ptc: "ADT",
            age: 30,
        });
    }

    // Add child passengers
    for (let i = 0; i < children; i++) {
        passengers.push({
            pax_id: `CHD${i + 1}`,
            expected_ptc: "CHD",
            age: 10,
        });
    }

    // Add infant passengers
    for (let i = 0; i < infants; i++) {
        passengers.push({
            pax_id: `INF${i + 1}`,
            expected_ptc: "INF",
            age: 1,
        });
    }

    return {
        metadata: {
            country: "IS",
            language: "en",
            currency: "USD",
        },
        origin_destinations: [
            {
                departure: {
                    airport_code: origin,
                    date: departureDate,
                },
                arrival: {
                    airport_code: destination,
                },
            },
        ],
        passengers,
    };
};

// Encode search params for URL
export const encodeSearchParams = (
    origin: string,
    destination: string,
    departureDate: string,
    adults: number,
    children: number = 0,
    infants: number = 0
): string => {
    // Format: OW_ORIGIN-DEST-YYYYMMDD_aXcYiZ
    const dateStr = departureDate.replace(/-/g, "");
    return `OW_${origin}-${destination}-${dateStr}_a${adults}c${children}i${infants}`;
};

// Decode search params from URL
export const decodeSearchParams = (
    encoded: string
): {
    origin: string;
    destination: string;
    departureDate: string;
    adults: number;
    children: number;
    infants: number;
} | null => {
    try {
        // Format: OW_ORIGIN-DEST-YYYYMMDD_aXcYiZ
        const match = encoded.match(
            /^OW_([A-Z]{3})-([A-Z]{3})-(\d{8})_a(\d+)c(\d+)i(\d+)$/
        );
        if (!match) return null;

        const [, origin, destination, dateStr, adults, children, infants] =
            match;
        const departureDate = `${dateStr.slice(0, 4)}-${dateStr.slice(
            4,
            6
        )}-${dateStr.slice(6, 8)}`;

        return {
            origin,
            destination,
            departureDate,
            adults: parseInt(adults, 10),
            children: parseInt(children, 10),
            infants: parseInt(infants, 10),
        };
    } catch {
        return null;
    }
};

// Format duration from seconds to human-readable string
export const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
};

// Format time from ISO 8601 to HH:MM
export const formatTime = (datetime: string): string => {
    const date = new Date(datetime);
    return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
};

// Format date for display
export const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
    });
};

// Available airports for demo (verified working routes in staging)
// Working routes: KEF↔CDG/LHR/JFK/AMS/FRA/CPH, EDI→MAD/LHR, MAN→MAD, LHR↔CDG/AMS
// Additional routes: INV↔BIO, TLN↔MXP/ALC
export const DEMO_AIRPORTS: AirportOption[] = [
    // Iceland hub
    {
        code: "KEF",
        name: "Keflavik International",
        city: "Reykjavik",
        country: "Iceland",
    },
    // UK airports
    { code: "LHR", name: "Heathrow Airport", city: "London", country: "UK" },
    {
        code: "EDI",
        name: "Edinburgh Airport",
        city: "Edinburgh",
        country: "UK",
    },
    {
        code: "MAN",
        name: "Manchester Airport",
        city: "Manchester",
        country: "UK",
    },
    {
        code: "INV",
        name: "Inverness Airport",
        city: "Inverness",
        country: "UK",
    },
    // European airports
    {
        code: "CDG",
        name: "Charles de Gaulle Airport",
        city: "Paris",
        country: "France",
    },
    {
        code: "AMS",
        name: "Schiphol Airport",
        city: "Amsterdam",
        country: "Netherlands",
    },
    {
        code: "FRA",
        name: "Frankfurt Airport",
        city: "Frankfurt",
        country: "Germany",
    },
    {
        code: "CPH",
        name: "Copenhagen Airport",
        city: "Copenhagen",
        country: "Denmark",
    },
    {
        code: "MAD",
        name: "Adolfo Suarez Madrid-Barajas",
        city: "Madrid",
        country: "Spain",
    },
    {
        code: "BIO",
        name: "Bilbao Airport",
        city: "Bilbao",
        country: "Spain",
    },
    {
        code: "ALC",
        name: "Alicante-Elche Airport",
        city: "Alicante",
        country: "Spain",
    },
    {
        code: "TLN",
        name: "Toulon-Hyeres Airport",
        city: "Toulon",
        country: "France",
    },
    {
        code: "MXP",
        name: "Milan Malpensa Airport",
        city: "Milan",
        country: "Italy",
    },
    // US airports
    {
        code: "JFK",
        name: "John F. Kennedy International",
        city: "New York",
        country: "USA",
    },
];

// Get airport by code
export const getAirportByCode = (code: string): AirportOption | undefined => {
    return DEMO_AIRPORTS.find((airport) => airport.code === code);
};
